import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  Coins,
  Flame,
  ShieldCheck,
  Percent,
  Compass,
  Sparkles,
  Zap,
  Leaf,
  ChevronRight,
  Info
} from 'lucide-react';

interface SavingsDataPoint {
  month: string;
  'Fuel Savings (₦)': number;
  'Grid Tariff Savings (₦)': number;
  'Cumulative Savings (₦)': number;
  'Net Investment State (₦)': number;
}

export const ProjectedSavingsChart: React.FC = () => {
  // Configurable presets representing standard Nigerian hybrid solar package scales
  const systemPresets = [
    {
      id: 'lite',
      name: '1.5kVA / 2.5kWh Lite',
      costNgn: 1250000,
      monthlyFuelSavingsLiters: 110, // liters of fuel saved per month
      monthlyGridSavingsKwh: 120, // kWh offset
      description: 'Offsets small generator (I-pass-my-neighbor) & basic fan/light grid load.'
    },
    {
      id: 'heavy',
      name: '3.5kVA / 5.0kWh Heavy-Duty',
      costNgn: 2850000,
      monthlyFuelSavingsLiters: 240, // liters saved
      monthlyGridSavingsKwh: 350, // kWh offset
      description: 'Replaces medium 2.5kVA–5kVA generators & runs fridge/freezer constantly.'
    },
    {
      id: 'premium',
      name: '7.5kVA / 10.0kWh Duplex',
      costNgn: 6450000,
      monthlyFuelSavingsLiters: 520, // liters saved
      monthlyGridSavingsKwh: 800, // kWh offset
      description: 'Replaces heavy diesel/petrol generators & handles inverter ACs.'
    }
  ];

  // Component state
  const [selectedPresetId, setSelectedPresetId] = useState<string>('heavy');
  const [fuelPricePerLiter, setFuelPricePerLiter] = useState<number>(1150); // Current average price per liter in NGN
  const [gridTariffPerKwh, setGridTariffPerKwh] = useState<number>(225); // Standard Band A tariff in NGN
  const [chartType, setChartType] = useState<'cumulative' | 'monthly'>('cumulative');

  // Selected preset metadata
  const activePreset = useMemo(() => {
    return systemPresets.find(p => p.id === selectedPresetId) || systemPresets[1];
  }, [selectedPresetId]);

  // Compute 12-month projections
  const projectionMetrics = useMemo(() => {
    const monthlyFuelSavingsNgn = activePreset.monthlyFuelSavingsLiters * fuelPricePerLiter;
    const monthlyGridSavingsNgn = activePreset.monthlyGridSavingsKwh * gridTariffPerKwh;
    const totalMonthlySavingsNgn = monthlyFuelSavingsNgn + monthlyGridSavingsNgn;
    const initialCost = activePreset.costNgn;

    const data: SavingsDataPoint[] = [];
    let cumSavings = 0;

    for (let monthNum = 1; monthNum <= 12; monthNum++) {
      cumSavings += totalMonthlySavingsNgn;
      data.push({
        month: `Month ${monthNum}`,
        'Fuel Savings (₦)': monthlyFuelSavingsNgn,
        'Grid Tariff Savings (₦)': monthlyGridSavingsNgn,
        'Cumulative Savings (₦)': cumSavings,
        'Net Investment State (₦)': cumSavings - initialCost
      });
    }

    const simplePaybackMonths = Number((initialCost / totalMonthlySavingsNgn).toFixed(1));
    const annualSavings = totalMonthlySavingsNgn * 12;
    const roiPercent = Number(((annualSavings / initialCost) * 100).toFixed(1));
    const co2SavedKg = Math.round(activePreset.monthlyFuelSavingsLiters * 2.31 * 12); // ~2.31 kg CO2 per liter of fuel

    return {
      data,
      monthlyFuelSavingsNgn,
      monthlyGridSavingsNgn,
      totalMonthlySavingsNgn,
      simplePaybackMonths,
      annualSavings,
      roiPercent,
      co2SavedKg
    };
  }, [activePreset, fuelPricePerLiter, gridTariffPerKwh]);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl space-y-6 font-sans">
      
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400">
              <TrendingUp className="w-5 h-5" />
            </span>
            <h2 className="text-xl font-black text-white flex items-center gap-2">
              Projected Customer Energy Savings
              <span className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">
                12-Month ROI Pitch tool
              </span>
            </h2>
          </div>
          <p className="text-xs text-slate-400">
            Interactive modeling engine designed to help sales reps close contracts by proving cumulative diesel & Band A tariff offset value.
          </p>
        </div>

        {/* Chart type toggle */}
        <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800 shrink-0 self-start md:self-center">
          <button
            onClick={() => setChartType('cumulative')}
            className={`text-[10px] font-extrabold px-3 py-1.5 rounded-lg transition-all ${chartType === 'cumulative' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Cumulative ROI
          </button>
          <button
            onClick={() => setChartType('monthly')}
            className={`text-[10px] font-extrabold px-3 py-1.5 rounded-lg transition-all ${chartType === 'monthly' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
          >
            Monthly Breakout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Interactive Control Bento Block */}
        <div className="lg:col-span-4 space-y-6 flex flex-col justify-between">
          <div className="space-y-5">
            
            {/* System Size Preset Selector */}
            <div className="space-y-2">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block">
                Sized System Configuration
              </span>
              <div className="grid grid-cols-1 gap-2.5">
                {systemPresets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => setSelectedPresetId(preset.id)}
                    className={`text-left p-3 rounded-2xl border transition-all duration-300 relative ${selectedPresetId === preset.id ? 'border-emerald-500 bg-emerald-950/10 shadow-lg shadow-emerald-500/5' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-extrabold text-white text-xs">{preset.name}</span>
                      <span className="text-[11px] font-bold font-mono text-emerald-400">
                        ₦{(preset.costNgn / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 leading-normal">{preset.description}</p>
                    {selectedPresetId === preset.id && (
                      <span className="absolute bottom-2 right-2 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Sliders representing typical volatility in Nigeria */}
            <div className="space-y-4 bg-slate-950/40 p-4 rounded-2xl border border-slate-800/80">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block">
                Macro-Economic cost variables
              </span>

              {/* Fuel price selector */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-slate-400 font-bold uppercase flex items-center gap-1">
                    <Flame className="w-3.5 h-3.5 text-amber-500" /> Petrol Cost / Liter
                  </span>
                  <span className="text-amber-400 font-extrabold font-mono">₦{fuelPricePerLiter}/L</span>
                </div>
                <input
                  type="range"
                  min="650"
                  max="1600"
                  step="50"
                  value={fuelPricePerLiter}
                  onChange={(e) => setFuelPricePerLiter(Number(e.target.value))}
                  className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
                <div className="flex justify-between text-[8px] text-slate-500 font-mono">
                  <span>₦650</span>
                  <span>₦1,150 (Avg)</span>
                  <span>₦1,600</span>
                </div>
              </div>

              {/* Grid tariff selector */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-slate-400 font-bold uppercase flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5 text-cyan-400" /> Band A Grid Tariff / kWh
                  </span>
                  <span className="text-cyan-400 font-extrabold font-mono">₦{gridTariffPerKwh}/kWh</span>
                </div>
                <input
                  type="range"
                  min="68"
                  max="350"
                  step="10"
                  value={gridTariffPerKwh}
                  onChange={(e) => setGridTariffPerKwh(Number(e.target.value))}
                  className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
                <div className="flex justify-between text-[8px] text-slate-500 font-mono">
                  <span>₦68 (Band D/E)</span>
                  <span>₦225 (Band A)</span>
                  <span>₦350</span>
                </div>
              </div>
            </div>

          </div>

          <div className="bg-slate-950/60 p-3.5 rounded-xl border border-slate-800 text-[10px] text-slate-400 flex gap-2 leading-relaxed">
            <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>
              Adjust these parameters during live consultation calls with potential clients to dynamically render the ROI models below.
            </span>
          </div>
        </div>

        {/* Right Recharts Area and Projection Metric Cards Block */}
        <div className="lg:col-span-8 flex flex-col justify-between gap-6">
          
          {/* Key ROI Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            
            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-1">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Monthly Savings</span>
              <div className="text-lg font-black text-white font-mono">
                ₦{projectionMetrics.totalMonthlySavingsNgn.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <span className="text-[9px] text-slate-400 block">Fuel + Grid combined</span>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-1">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Projected Payback</span>
              <div className="text-lg font-black text-amber-400 font-mono flex items-baseline gap-1">
                {projectionMetrics.simplePaybackMonths} <span className="text-xs text-slate-400 font-sans">Months</span>
              </div>
              <span className="text-[9px] text-slate-400 block">Full system break-even</span>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-1">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">1st Year Yield (ROI)</span>
              <div className="text-lg font-black text-emerald-400 font-mono">
                {projectionMetrics.roiPercent}%
              </div>
              <span className="text-[9px] text-emerald-500/80 font-bold block">Outperforms local banks</span>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-1">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Carbon Avoided</span>
              <div className="text-lg font-black text-cyan-400 font-mono flex items-center gap-1">
                <Leaf className="w-4 h-4 text-emerald-400 fill-current" /> {projectionMetrics.co2SavedKg.toLocaleString()} <span className="text-xs text-slate-400 font-sans">kg</span>
              </div>
              <span className="text-[9px] text-slate-400 block">Clean energy offset</span>
            </div>

          </div>

          {/* Recharts Graphical Line Visualization */}
          <div className="bg-slate-950/40 border border-slate-800/80 rounded-2xl p-5 flex flex-col justify-between min-h-[300px]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  {chartType === 'cumulative' ? '12-Month Cumulative Financial Yield Trajectory' : 'Fuel Savings vs Grid Savings Monthly Breakdown'}
                </h3>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {chartType === 'cumulative' 
                    ? 'Break-even point is hit when Cumulative Savings cross initial capital investment.'
                    : 'A steady monthly comparison of cost avoidance components.'}
                </p>
              </div>
            </div>

            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'cumulative' ? (
                  <AreaChart
                    data={projectionMetrics.data}
                    margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorCumulative" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" stroke="#475569" fontSize={10} />
                    <YAxis 
                      stroke="#475569" 
                      fontSize={10} 
                      tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`} 
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px' }}
                      formatter={(value: any) => [`₦${Math.round(Number(value)).toLocaleString()}`, '']}
                    />
                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '10px' }} />
                    <Area 
                      type="monotone" 
                      name="Cumulative Savings (₦)" 
                      dataKey="Cumulative Savings (₦)" 
                      stroke="#10b981" 
                      strokeWidth={2.5}
                      fillOpacity={1} 
                      fill="url(#colorCumulative)" 
                    />
                    <Area 
                      type="monotone" 
                      name="Net Value State (₦)" 
                      dataKey="Net Investment State (₦)" 
                      stroke="#3b82f6" 
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      fillOpacity={1} 
                      fill="url(#colorNet)" 
                    />
                  </AreaChart>
                ) : (
                  <LineChart
                    data={projectionMetrics.data}
                    margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" stroke="#475569" fontSize={10} />
                    <YAxis 
                      stroke="#475569" 
                      fontSize={10} 
                      tickFormatter={(value) => `₦${(value / 1000).toFixed(0)}k`} 
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '12px' }}
                      formatter={(value: any) => [`₦${Math.round(Number(value)).toLocaleString()}`, '']}
                    />
                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '10px' }} />
                    <Line 
                      type="monotone" 
                      name="Fuel Savings/mo (₦)" 
                      dataKey="Fuel Savings (₦)" 
                      stroke="#f59e0b" 
                      strokeWidth={2.5} 
                    />
                    <Line 
                      type="monotone" 
                      name="Grid Savings/mo (₦)" 
                      dataKey="Grid Tariff Savings (₦)" 
                      stroke="#06b6d4" 
                      strokeWidth={2.5} 
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* Payback milestone text */}
            <div className="flex items-center justify-between gap-3 border-t border-slate-800 pt-4 text-xs">
              <span className="text-slate-400 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-400" /> Payback Target Achieved In:
              </span>
              <span className="font-extrabold text-white bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-xl text-xs font-mono">
                {projectionMetrics.simplePaybackMonths} Months
              </span>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
};
